/**
 * Apple/WebKit audio-playback helpers.
 *
 * WebKit (Safari on macOS and *every* browser on iOS) cannot decode
 * Opus-in-WebM in an `<audio>` element, and `canPlayType('audio/webm;
 * codecs="opus"')` LIES about it — it reports "probably" yet fails to play. So
 * we force an AAC/MP4 variant on any Apple engine, and on any non-Apple engine
 * that doesn't confidently answer "probably". The backend serves that variant
 * from the same audio URL with `?format=m4a` (transcoded + cached on demand).
 *
 * Single source of truth: every surface that plays a meeting recording
 * (the main Player, the speaker voice-sample buttons, …) must route through
 * here so a new playback surface can't silently regress Apple support.
 */

/** True when this engine should play the AAC/MP4 (`?format=m4a`) variant. */
export function prefersM4aPlayback(): boolean {
  if (typeof navigator === "undefined" || typeof document === "undefined") return false
  if (/apple/i.test(navigator.vendor)) return true
  return document.createElement("audio").canPlayType('audio/webm; codecs="opus"') !== "probably"
}

/** Append the `format=m4a` query param to an audio source URL. */
export function toM4aUrl(src: string): string {
  return `${src}${src.includes("?") ? "&" : "?"}format=m4a`
}

/**
 * Resolve the source a plain `<audio>`/`new Audio()` surface should load:
 * the AAC/MP4 variant on Apple/WebKit, the original otherwise. Use this for
 * simple players that don't run the main Player's transcode-readiness poll.
 */
export function resolvePlaybackSrc(src: string): string {
  return prefersM4aPlayback() ? toM4aUrl(src) : src
}
